import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NetworkIcon, CableIcon, DatabaseIcon, MailIcon, VideoIcon, SmartphoneIcon, ShieldCheckIcon, BlocksIcon, ArrowRightIcon, LockIcon, ShieldIcon, UserCheckIcon, CloudIcon, FileCheckIcon, GlobeIcon, ClipboardCheckIcon, PenToolIcon, WrenchIcon, HeadphonesIcon, AlertTriangleIcon, TrendingUpIcon, DollarSignIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
const services = [{
  title: 'Networking',
  description: 'Secure, high-performance networking solutions to boost connectivity and ensure reliable business communication.',
  icon: NetworkIcon,
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  title: 'Cat 5 / Cat 6 Cabling',
  description: 'Expert installation of Cat 5 and Cat 6 cabling, ensuring fast and dependable network performance.',
  icon: CableIcon,
  color: 'text-teal-500',
  bg: 'bg-teal-50'
}, {
  title: 'Data Backup and Recovery',
  description: 'Securely back up and quickly restore your critical data, minimizing downtime and ensuring continuity.',
  icon: DatabaseIcon,
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  title: 'Microsoft Office 365',
  description: 'Complete management of Microsoft Office 365 for improved productivity and seamless collaboration.',
  icon: MailIcon,
  color: 'text-orange-500',
  bg: 'bg-orange-50'
}, {
  title: 'Multi-Site Video Security System',
  description: 'Centralized video security for monitoring and managing multiple locations with ease.',
  icon: VideoIcon,
  color: 'text-red-500',
  bg: 'bg-red-50'
}, {
  title: 'Smart Device Control',
  description: 'Remote management of smart devices to optimize operations, enhance security, and provide greater control over your business environment.',
  icon: SmartphoneIcon,
  color: 'text-violet-500',
  bg: 'bg-violet-50'
}, {
  title: 'Surveillance Security Systems',
  description: 'Advanced security systems installation to safeguard your business premises effectively.',
  icon: ShieldCheckIcon,
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}, {
  title: 'App Integration',
  description: 'Efficient application integration to streamline business processes and enhance functionality.',
  icon: BlocksIcon,
  color: 'text-pink-500',
  bg: 'bg-pink-50'
}];
const securityLayers = [{
  icon: ShieldIcon,
  title: 'Next-Gen Firewall',
  desc: 'Advanced perimeter defense with deep packet inspection and threat prevention.'
}, {
  icon: UserCheckIcon,
  title: 'Endpoint Protection',
  desc: 'Secure every device on your network with managed antivirus and EDR.'
}, {
  icon: MailIcon,
  title: 'Email Security',
  desc: 'Protection against phishing, malware, and business email compromise.'
}, {
  icon: CloudIcon,
  title: 'Cloud Protection',
  desc: 'Secure your SaaS applications and cloud infrastructure from external threats.'
}];
const complianceItems = [{
  title: 'HIPAA',
  desc: 'Healthcare data protection and privacy standards compliance.'
}, {
  title: 'PCI-DSS',
  desc: 'Secure payment card processing and transaction environments.'
}, {
  title: 'GDPR',
  desc: 'International data privacy and protection regulation readiness.'
}, {
  title: 'SOC 2',
  desc: 'Trust services criteria for security, availability, and confidentiality.'
}];
const processSteps = [{
  icon: ClipboardCheckIcon,
  title: 'Audit',
  desc: 'Comprehensive vulnerability scanning and network assessment.'
}, {
  icon: PenToolIcon,
  title: 'Design',
  desc: 'Multi-layered security architecture tailored to your risk profile.'
}, {
  icon: WrenchIcon,
  title: 'Install',
  desc: 'Deployment of security hardware and software configurations.'
}, {
  icon: HeadphonesIcon,
  title: 'Support',
  desc: 'Proactive monitoring and rapid incident response 24/7.'
}];
export function NetworkSecurityPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div className="bg-white" data-id="element-1610">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-1611">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1612"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1613"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-red-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-1614"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1615">
          <div className="max-w-3xl" data-id="element-1616">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1617">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1618">
                <span className="flex h-2 w-2 rounded-full bg-red-400 mr-2" data-id="element-1619"></span>
                Comprehensive IT & Security Solutions
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1620">
                Enterprise-Grade
                <br className="hidden md:block" data-id="element-1621" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1622">
                  Network Security
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-1623">
                We understand the critical importance of safeguarding your
                business's digital infrastructure. Our network security
                management services are designed to protect your data, ensure
                seamless connectivity, and provide peace of mind.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1624">
                Secure Your Network
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1625" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white" data-id="element-1626">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1627">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1628">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1629">
              Our Core Services
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1630">
              With a focus on cutting-edge technology and expert support, we
              deliver tailored solutions to meet the unique needs of your
              organization.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1631">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 flex flex-col hover:bg-white hover:shadow-xl transition-all" data-id="element-1632">
                  <div className={`w-12 h-12 ${service.bg} rounded-xl flex items-center justify-center mb-5`} data-id="element-1633">
                    <Icon className={`w-6 h-6 ${service.color}`} data-id="element-1634" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-1635">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed flex-grow" data-id="element-1636">
                    {service.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Threat Landscape */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-1637">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1638"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1639">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1640">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1641">
              The Modern Threat Landscape
            </h2>
            <p className="text-lg text-slate-400" data-id="element-1642">
              Cyber attacks are no longer just an enterprise problem. Small and
              medium businesses are prime targets.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1643">
            {[{
            icon: AlertTriangleIcon,
            stat: '43%',
            desc: 'Of all cyber attacks target small to medium businesses.'
          }, {
            icon: DollarSignIcon,
            stat: '$4.45M',
            desc: 'Is the average total cost of a data breach globally.'
          }, {
            icon: TrendingUpIcon,
            stat: '300%',
            desc: 'Increase in ransomware attacks over the last two years.'
          }, {
            icon: ShieldIcon,
            stat: '60%',
            desc: 'Of SMBs close within 6 months of a major cyber attack.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-id="element-1644">
                <item.icon className="w-10 h-10 text-red-400 mx-auto mb-4" data-id="element-1645" />
                <div className="text-4xl font-black text-white mb-2" data-id="element-1646">
                  {item.stat}
                </div>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-1647">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Security Layers */}
      <section className="py-24 bg-slate-50" data-id="element-1648">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1649">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1650">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1651">
              Multi-Layered Defense
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1652">
              Security is not a single product, but a comprehensive strategy. We
              protect every vector of your digital environment.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1653">
            {securityLayers.map((layer, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 text-center" data-id="element-1654">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1655">
                  <layer.icon className="w-8 h-8 text-primary" data-id="element-1656" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1657">
                  {layer.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed" data-id="element-1658">
                  {layer.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Incident Response Plan */}
      <section className="py-24 bg-white" data-id="element-1659">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1660">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1661">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1662">
              Rapid Incident Response
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1663">
              When a threat is detected, every second counts. Our SOC follows a
              strict, proven response protocol.
            </p>
          </div>
          <div className="relative" data-id="element-1664">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-slate-100 -translate-y-1/2 z-0" data-id="element-1665"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10" data-id="element-1666">
              {[{
              time: 'Minutes',
              title: '1. Detection',
              desc: 'AI-driven tools identify anomalous behavior instantly.'
            }, {
              time: 'Hours',
              title: '2. Containment',
              desc: 'Infected endpoints are isolated from the network to prevent spread.'
            }, {
              time: 'Days',
              title: '3. Eradication',
              desc: 'Malware is removed and vulnerabilities are patched.'
            }, {
              time: 'Ongoing',
              title: '4. Recovery',
              desc: 'Data is restored from backups and systems return to normal.'
            }].map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm text-center" data-id="element-1667">
                  <div className="text-xs font-bold text-red-500 uppercase tracking-widest mb-3" data-id="element-1668">
                    {step.time}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1669">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-1670">{step.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative" data-id="element-1671">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[100px]" data-id="element-1672"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1673">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1674">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1675">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1676">
                Regulatory Compliance Ready
              </h2>
              <p className="text-slate-400 text-lg mb-8" data-id="element-1677">
                Meeting industry standards is no longer optional. Our security
                frameworks are built to align with major regulatory
                requirements, helping you avoid fines and protect your
                reputation.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1678">
                {complianceItems.map((item, i) => <div key={i} className="flex items-start gap-3 bg-white/5 p-4 rounded-xl border border-white/10" data-id="element-1679">
                    <FileCheckIcon className="w-5 h-5 text-primary shrink-0" data-id="element-1680" />
                    <div data-id="element-1681">
                      <h4 className="font-bold text-white" data-id="element-1682">{item.title}</h4>
                      <p className="text-xs text-slate-400" data-id="element-1683">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="relative" data-id="element-1684">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 p-10 rounded-3xl" data-id="element-1685">
                <GlobeIcon className="w-16 h-16 text-primary mb-6" data-id="element-1686" />
                <h3 className="text-2xl font-bold mb-4" data-id="element-1687">
                  Global Threat Intelligence
                </h3>
                <p className="text-slate-400 mb-6" data-id="element-1688">
                  Our security operations center (SOC) leverages global threat
                  feeds to block emerging attacks before they reach your
                  network.
                </p>
                <div className="space-y-3" data-id="element-1689">
                  {['Real-time IP Reputation', 'Zero-day Protection', 'Behavioral Analysis'].map((t, i) => <div key={i} className="flex items-center gap-3" data-id="element-1690">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" data-id="element-1691"></div>
                      <span className="text-sm text-slate-300" data-id="element-1692">{t}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-1693">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1694">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1695">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1696">
              Our Security Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1697">
              We take a systematic approach to hardening your network against
              modern threats.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-1698">
            {processSteps.map((step, i) => <div key={i} className="text-center" data-id="element-1699">
                <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-slate-100" data-id="element-1700">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-1701" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1702">
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1703">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Security FAQ */}
      <section className="py-24 bg-slate-50" data-id="element-1704">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1705">
          <div className="text-center mb-16" data-id="element-1706">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-1707">
              <HelpCircleIcon className="w-8 h-8 text-primary" data-id="element-1708" />
              Cybersecurity FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1709">
              Common questions about protecting your business.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1710">
            {[{
            q: 'Do I need a firewall if I have antivirus?',
            a: 'Yes. Antivirus protects individual devices from known malware, while a firewall acts as a gatekeeper for your entire network, blocking unauthorized traffic before it even reaches your devices.'
          }, {
            q: 'How often should we do security audits?',
            a: 'We recommend a comprehensive security audit at least annually, or whenever you make significant changes to your IT infrastructure. Vulnerability scanning should be done continuously.'
          }, {
            q: 'What is EDR?',
            a: 'Endpoint Detection and Response (EDR) goes beyond traditional antivirus. It monitors device behavior in real-time to detect and stop advanced threats like ransomware, even if the malware has never been seen before.'
          }, {
            q: 'Do you offer employee security training?',
            a: 'Yes. Human error is the leading cause of data breaches. We provide automated phishing simulations and security awareness training to turn your employees into your strongest defense.'
          }, {
            q: 'What about cloud security?',
            a: "Moving to the cloud doesn't automatically make you secure. We implement cloud security posture management (CSPM) and secure your Microsoft 365/Google Workspace environments against account takeovers."
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-1711">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-1712">
                  <span className="font-bold text-slate-900" data-id="element-1713">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-1714">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-1715" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-1716">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-1717">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-1718">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-1719">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-1720">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-1721"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-1722"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-1723">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1724">
            Secure Your Business Today!
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-1725">
            We're committed to providing top-tier network security management
            and IT services to help your business thrive in today's digital
            landscape. Let us be your trusted partner in safeguarding your
            network and ensuring uninterrupted business operations.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1726">
            <button onClick={() => onNavigate('contact')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-1727">
              Get Your Free Assessment
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1728" />
            </button>
            <a href="tel:+4105255284" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-1729">
              Call Us: 410-525-5284
            </a>
          </div>
        </div>
      </section>
    </div>;
}